import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, Expand } from "lucide-react";
import { PROJECTS } from "@/data/site";
import { SECTION, BTN_PRIMARY } from "./styles";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { BeforeAfterSlider } from "./BeforeAfter";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (p) => (p && p.startsWith("/api") ? `${BACKEND}${p}` : p);

export const Projects = () => {
  const [active, setActive] = useState(null);
  const [projects, setProjects] = useState(PROJECTS);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/projects`);
        if (Array.isArray(data) && data.length) setProjects(data);
      } catch {
        // keep static fallback
      }
    })();
  }, []);

  return (
    <section id="projects" data-testid="projects-section" className={`${SECTION} bg-white`}>
      <div className="mb-16">
        <p className="font-accent-script text-2xl md:text-3xl text-[#D4AF37] mb-3">
          From Vision To Reality
        </p>
        <h2
          className="font-serif text-3xl md:text-5xl font-semibold text-[#0A1F3C] gold-underline"
          data-testid="projects-headline"
        >
          OUR WORK
        </h2>
        <p className="text-[#5A6A85] mt-6 max-w-2xl leading-relaxed">
          Real before-and-after transformations from our recent projects across Central Virginia.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {projects.map((p) => (
          <article
            key={p.id}
            data-testid={`project-card-${p.id}`}
            className="group bg-[#F7F7F4] border border-[#E2E2DE] hover:border-[#D4AF37] transition-colors duration-300 flex flex-col"
          >
            <button
              onClick={() => setActive(p)}
              data-testid={`project-view-btn-${p.id}`}
              className="relative h-64 overflow-hidden text-left"
              aria-label={`View ${p.title}`}
            >
              <img
                src={fileUrl(p.img)}
                alt={`${p.title} — before and after`}
                loading="lazy"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <span className="absolute bottom-3 right-3 bg-white/90 border border-[#D4AF37] text-[#C9A227] p-2">
                <Expand className="h-4 w-4" />
              </span>
            </button>
            <div className="p-6 flex flex-col flex-1">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-2">
                <MapPin className="h-3 w-3" /> {p.location}
              </div>
              <h3 className="font-serif text-2xl text-[#0A1F3C] mb-3">{p.title}</h3>
              <p className="text-sm text-[#5A6A85] leading-relaxed flex-1">{p.description || p.desc}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {(p.services || []).map((s) => (
                  <span
                    key={s}
                    className="text-[10px] uppercase tracking-widest text-[#44506A] border border-[#D9D9D4] px-2 py-1"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-14">
        <a href="#estimate" data-testid="projects-request-estimate-btn" className={BTN_PRIMARY}>
          Request an Estimate
        </a>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="bg-white border border-[#D4AF37] max-w-4xl p-2">
          <DialogTitle className="sr-only">{active?.title}</DialogTitle>
          {active && (active.before_img && active.after_img ? (
            <BeforeAfterSlider
              before={fileUrl(active.before_img)}
              after={fileUrl(active.after_img)}
              title={active.title}
            />
          ) : (
            <img
              src={fileUrl(active.img)}
              alt={`${active.title} — full project board`}
              data-testid="project-lightbox-image"
              className="w-full h-auto"
            />
          ))}
        </DialogContent>
      </Dialog>
    </section>
  );
};
